import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'react';
import { CaretLeftIcon, CaretRightIcon } from '@radix-ui/react-icons';
import ImageView from '@/components/ui/trainers/shared/ImageView';
import { IFrame } from '@/utils/trainers';
import { ILandmark } from '@/types/features';
import { Checkbox } from '@/components/shadcn/checkbox';

interface Props {
  landmarksAssets: {
    landmarks: ILandmark[];
    landmarkToAssets: Record<string, any>;
  };
  setDetectionViewerHeight: Dispatch<SetStateAction<number>>;
  setCurrentFrameIdx: Dispatch<SetStateAction<number | undefined>>;
  setSelectedLandmark: Dispatch<SetStateAction<string | null>>;
  doNotShowOtherDetections?: boolean;
}

const getInitialFrame = (frames: IFrame[]): number => {
  const offset = frames.length <= 2 ? 1 : 2;
  return frames.length - offset;
};

export const getBoundingBox = (
  detection?:
    | [number, number, number, number]
    | [string, number, number, number, number],
): [number, number, number, number] => {
  const defaultBoundingBox: [number, number, number, number] = [0, 0, 0, 0];
  if (!detection) {
    return defaultBoundingBox;
  }

  let centerX, centerY, width, height;
  const margin = 0.5;
  if (detection.length === 4) {
    centerX = detection[0] || 0;
    centerY = detection[1] || 0;
    width = detection[2] || 0;
    height = detection[3] || 0;
  } else if (detection.length > 4) {
    centerX = detection[1] || 0;
    centerY = detection[2] || 0;
    width = detection[3] || 0;
    height = detection[4] || 0;
  } else {
    return defaultBoundingBox;
  }

  const w = (width + width * margin) * 100;
  const h = (height + height * margin) * 100;
  const x = centerX * 100 - w / 2;
  const y = centerY * 100 - h / 2;

  return [x, y, w, h];
};

export const DetectionViewer: React.FC<Props> = ({
  landmarksAssets,
  setDetectionViewerHeight,
  setCurrentFrameIdx,
  setSelectedLandmark,
  doNotShowOtherDetections,
}) => {
  const detectionViewer = useRef<HTMLDivElement | null>(null);
  const [showOtherDetections, setShowOtherDetections] = useState(false);

  const orderedLandmarks = landmarksAssets?.landmarks.sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );

  const [currentLandmark, setCurrentLandmark] = useState(
    orderedLandmarks.length - 1,
  );

  const currentLandmarkObject = orderedLandmarks[currentLandmark];

  const frames: IFrame[] = useMemo(() => {
    if (currentLandmarkObject) {
      const framesArray: IFrame[] =
        landmarksAssets?.landmarkToAssets[currentLandmarkObject._id].frames ||
        [];
      framesArray.sort((a, b) => a.idx - b.idx);
      return framesArray;
    }
    return [];
  }, [currentLandmarkObject, landmarksAssets]);

  const detectionsByFrame = currentLandmarkObject
    ? landmarksAssets?.landmarkToAssets[currentLandmarkObject._id]
        .detectionsByFrame
    : {};

  const [currentFrame, setCurrentFrame] = useState(() =>
    getInitialFrame(frames),
  );
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  const currentFrameIdx = frames[currentFrame]?.idx;
  const currentDetection = detectionsByFrame?.[currentFrameIdx]?.[0];

  const { detectionInfo, otherDetectionsInfo } = currentDetection || {};
  const [x, y, w, h] = getBoundingBox(detectionInfo);
  const otherDetections = (otherDetectionsInfo ?? []).map(
    (detection: [string, number, number, number, number]) =>
      getBoundingBox(detection),
  );

  const updateCurrentFrame = useCallback(
    (frame: number, landmark?: ILandmark) => {
      let _frames = frames;
      let _frame = frame;
      if (landmark) {
        _frames = landmarksAssets?.landmarkToAssets[landmark._id].frames || [];
        _frame = getInitialFrame(_frames);
      }
      setCurrentFrameIdx?.(_frame);
      setCurrentFrame(_frame);
    },
    [frames, setCurrentFrameIdx, landmarksAssets],
  );

  const handleNext = () => {
    const nextFrame = currentFrame + 1 < frames.length ? currentFrame + 1 : 0;
    updateCurrentFrame(nextFrame);
  };

  const handlePrev = () => {
    const prevFrame =
      currentFrame - 1 >= 0 ? currentFrame - 1 : frames.length - 1;
    updateCurrentFrame(prevFrame);
  };

  const handleNextLandmark = () => {
    const nextLandmark =
      currentLandmark + 1 < orderedLandmarks.length ? currentLandmark + 1 : 0;
    updateCurrentFrame(getInitialFrame(frames), orderedLandmarks[nextLandmark]);
    setCurrentLandmark(nextLandmark);
  };

  const handlePrevLandmark = () => {
    const prevLandmark =
      currentLandmark - 1 >= 0
        ? currentLandmark - 1
        : orderedLandmarks.length - 1;
    updateCurrentFrame(getInitialFrame(frames), orderedLandmarks[prevLandmark]);
    setCurrentLandmark(prevLandmark);
  };

  useEffect(() => {
    updateCurrentFrame(getInitialFrame(frames));
  }, [frames, updateCurrentFrame]);

  useEffect(() => {
    const currLandmark = orderedLandmarks[currentLandmark];
    if (currLandmark) {
      setSelectedLandmark(currLandmark._id);
    }
  }, [currentLandmark, orderedLandmarks, setSelectedLandmark]);

  useEffect(() => {
    let mounted = true;
    if (frames.length > 0 && frames[currentFrame]) {
      const image = new Image();
      image.src = frames[currentFrame].url;

      image.onload = () => {
        if (mounted) {
          setIsImageLoaded(true);
        }
      };
    }
    return () => {
      mounted = false;
    };
  }, [frames, currentFrame]);

  useEffect(() => {
    if (isImageLoaded && detectionViewer.current) {
      setDetectionViewerHeight(detectionViewer.current.clientHeight);
    }
  }, [isImageLoaded, setDetectionViewerHeight]);

  useEffect(() => {
    return () => {
      setDetectionViewerHeight(0);
    };
  }, [setDetectionViewerHeight]);

  return (
    <div>
      {isImageLoaded && (
        <div
          ref={detectionViewer}
          className="flex w-full items-start justify-center">
          <div className="relative flex w-full">
            {frames.length > 0 && frames[currentFrame] && (
              <div>
                <div className="relative w-full leading-none">
                  <ImageView
                    src={frames[currentFrame].url}
                    boundingBox={[x, y, w, h]}
                    otherBoundingBoxes={
                      showOtherDetections ? otherDetections : []
                    }
                  />
                </div>
                <div className="absolute bottom-4 left-4">
                  {otherDetections.length && !doNotShowOtherDetections && (
                    <div className="mb-4 flex w-40 items-center justify-between rounded bg-neutral-700 px-4 py-2 text-xs">
                      <div>Display Other Detections</div>
                      <Checkbox
                        checked={showOtherDetections}
                        onClick={() => setShowOtherDetections(prev => !prev)}
                      />
                    </div>
                  )}
                  {orderedLandmarks?.length > 0 && (
                    <div className="mb-4 flex w-40 items-center justify-between rounded bg-neutral-700 px-4 py-2 text-xs">
                      <div>Landmark</div>
                      <div className="flex items-center">
                        <CaretLeftIcon onClick={handlePrevLandmark} />
                        <div className="w-12 text-center">
                          {currentLandmark + 1}/{orderedLandmarks.length}
                        </div>
                        <CaretRightIcon onClick={handleNextLandmark} />
                      </div>
                    </div>
                  )}
                  {frames.length > 0 && (
                    <div className="mb-4 flex w-40 items-center justify-between rounded bg-neutral-700 px-4 py-2 text-xs">
                      <div>Image</div>
                      <div className="flex items-center">
                        <CaretLeftIcon onClick={handlePrev} />
                        <div className="w-12 text-center">
                          {currentFrame + 1}/{frames.length}
                        </div>
                        <CaretRightIcon onClick={handleNext} />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
